import DocsConfig from '@tencent/docs-config';
import { ToolbarFileMenuConfig } from '@src/slide/infrastructure/module/dependent/config/config-manager-constants';
import { IBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/base-model.interface';
import { INoteModel } from '@src/slide/domain/ui-model/element-modeles/Interface/note-model.interface';
import { IPictureModel } from '@src/slide/domain/ui-model/element-modeles/Interface/picture-model.interface';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { IMenuItem } from '@src/slide/application/service/menu-manager/menu-items/menu-items.interface';
import { SHAPE_IMAGE_FILL, SHAPE_IMAGE_TILE_FILL, UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { TOOLBAR_IMAGE_TRANS } from '@editor/src/img/pageIndex/index';
import ToolbarManager, { SelectElementTypes } from '@editor/src/js/constants/toolbar-data';
import { ActionType } from '@editor/src/js/constants/common-action-type';
import i18n from '@i18next/i18n';
import { IChartModel } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-model.interface';

/**
 * 获取顶部工具栏
 * @param model 图形 uiModel
 */
export function getToolbarMenuItems(model: IBaseModel | INoteModel): IMenuItem[] {
    const modelType = model.type;
    // 空白 || 左侧缩略图间隔条
    if ([UIModelType.EMPTY, UIModelType.GAP].includes(modelType)) {
        return ToolbarManager.getGroupListByType(SelectElementTypes.NONE);
    }
    // 多选元素 || 组合
    if ([UIModelType.MULTIPLY, UIModelType.GROUP].includes(modelType)) {
        return ToolbarManager.getGroupListByType(SelectElementTypes.MULTIPLE);
    }
    // 连接线
    if ([UIModelType.CONNECTOR].includes(modelType)) {
        return ToolbarManager.getGroupListByType(SelectElementTypes.CONNECTOR);
    }
    // 表格
    if ([UIModelType.TABLE].includes(modelType)) {
        return ToolbarManager.getGroupListByType(SelectElementTypes.TABLE);
    }
    // 备注
    if ([UIModelType.NOTE].includes(modelType)) {
        return getNoteToolbarMenuItems();
    }
    // 图片
    if ([UIModelType.IMAGE].includes(modelType)) {
        return getImageToolbarMenuItems(model as IPictureModel);
    }
    // 形状
    if ([UIModelType.SHAPE].includes(modelType)) {
        return getShapeToolbarMenuItems(model as IShapeModel);
    }
    // 图表
    if ([UIModelType.CHART].includes(modelType)) {
        return getChartToolBarMenuItems(model as IChartModel);
    }
    return [];
}

/**
 * 获取备注顶部工具栏
 */
function getNoteToolbarMenuItems(): IMenuItem[] {
    const raw = ToolbarManager.getGroupListByType(SelectElementTypes.NOTE);
    const filterItemIds = [
        ActionType.VERTICAL_ALIGN,
        ActionType.FORMAT_BRUSH,
        ActionType.CLEAR_FORMAT,
        ActionType.ROW_SPACE,
    ];
    return raw.filter(item => !filterItemIds.includes(item.id));
}

/**
 * 获取图片顶部工具栏
 * @param model 图形 uiModel
 */
function getImageToolbarMenuItems(model: IPictureModel): IMenuItem[] {
    let items = ToolbarManager.getGroupListByType(SelectElementTypes.PICTURE);
    if (model.isMedia()) {  // 音视频移除裁剪能力
        // 支持裁剪的视频
        const enableTailor = model.isVideo() && DocsConfig.getInstance().getConfig(ToolbarFileMenuConfig.tailorVideo);
        const disableFeatures = [
            !enableTailor && ActionType.TAILOR_PIC,
            ActionType.TRANSPARENCY,
        ].filter(Boolean);
        items = items.filter(item => !disableFeatures.includes(item.id));
        items = ToolbarManager.filterNeighbourDivider(items);
    }
    return items;
}

/**
 * 获取形状顶部工具栏
 * @param model 图形 uiModel
 */
function getShapeToolbarMenuItems(model: IShapeModel): IMenuItem[] {
    let toolBarItems =  ToolbarManager.getGroupListByType(SelectElementTypes.SHAPE);
    const image = model.props?.style?.fillBackground?.image;
    if (image && !model.isTextMath) {
        if (image.tile) {
            toolBarItems =  ToolbarManager.getGroupListByType(SHAPE_IMAGE_FILL);
        } else {
            toolBarItems =  ToolbarManager.getGroupListByType(SHAPE_IMAGE_TILE_FILL);
        }
    }
    return toolBarItems;
}

function getChartToolBarMenuItems(model: IChartModel): IMenuItem[] {
    const toolBarItems =  ToolbarManager.getGroupListByType(SelectElementTypes.CHART);
    return toolBarItems;
}
